export function isPlainObject(obj: unknown): obj is Record<string, unknown> {
	let proto
	return (
		obj !== null &&
		typeof obj === 'object' &&
		((proto = Object.getPrototypeOf(obj)) === Object.prototype ||
			proto === null)
	)
}

export function deepEqual(a: unknown, b: unknown): boolean {
	// Handle strict equality and identical references
	if (a === b) {
		return true
	}

	// Handle null or undefined
	if (a == null || b == null) {
		return false
	}

	// Handle Date objects
	if (a instanceof Date && b instanceof Date) {
		return a.getTime() === b.getTime()
	}

	// Handle Arrays
	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) {
			return false
		}
		for (let i = 0; i < a.length; i++) {
			if (!deepEqual(a[i], b[i])) {
				return false
			}
		}
		return true
	}

	// Handle plain objects
	if (typeof a === 'object' && typeof b === 'object') {
		const keysA = Object.keys(a as Record<string, unknown>)
		const keysB = Object.keys(b as Record<string, unknown>)

		if (keysA.length !== keysB.length) {
			return false
		}

		for (const key of keysA) {
			if (!Object.prototype.hasOwnProperty.call(b, key)) {
				return false
			}
			if (
				!deepEqual(
					(a as Record<string, unknown>)[key],
					(b as Record<string, unknown>)[key]
				)
			) {
				return false
			}
		}
		return true
	}

	// For everything else (primitives, mismatched types)
	return false
}

export function mergeDeep(
	source: Record<string, unknown> = {},
	target: Record<string, unknown> = {},
	arrayFn?: (_a: unknown[], _b: unknown[]) => unknown[]
) {
	const out: Record<string, unknown> = {}

	for (const key in source) {
		out[key] = source[key]
	}

	for (const key in target) {
		const sourceProperty = source[key]
		const targetProperty = target[key]

		// Only continue deep merging if
		// both properties are plain objects
		if (isPlainObject(sourceProperty) && isPlainObject(targetProperty)) {
			out[key] = mergeDeep(sourceProperty, targetProperty, arrayFn)

			continue
		}

		if (
			arrayFn &&
			Array.isArray(sourceProperty) &&
			Array.isArray(targetProperty)
		) {
			out[key] = arrayFn(sourceProperty, targetProperty)

			continue
		}

		out[key] = targetProperty
	}

	return out
}
